import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { CancelDocumentsProps, CancelDocumentActionData } from "types/modals";
import { cancelDocument } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";
import { PATH_TO_DOCUMENT_OVERVIEW } from "components/router/paths";

const CancelDocuments = ({ more, closeModal, loopFocus, errorCallback }: CancelDocumentsProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const commentInputRef = useRef<HTMLTextAreaElement>(null);

    const [comment, setComment] = useState("");
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [actionRunning, setActionRunning] = useState(false);

    const handleCancelSuccess = useCallback(() => {
        closeModal();
        more.handledTaskCallback(undefined, more.navigate);
        more.navigate(PATH_TO_DOCUMENT_OVERVIEW);
    }, [more, closeModal]);

    const handleCancelError = useCallback((error: Record<string, any>) => {
        setDisplayError(error.error ?? error);
        setActionRunning(false);
    }, [errorCallback]);

    const handleCancel = useCallback(() => {
        setActionRunning(true);
        setDisplayError(null);

        const data: CancelDocumentActionData = {
            companyId: more.companyId,
            key: more.tasks[0].key,
            comment,
            cancelMessage: "",
        };

        dispatch(cancelDocument(data, handleCancelSuccess, handleCancelError));
    }, [comment, more, dispatch, closeModal, handleCancelSuccess, handleCancelError]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !actionRunning;

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={commentInputRef}
                    id="comment"
                    inputHint={translate("popUp.approve.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    value={comment}
                    propagateValue={handleCommentChange}
                    label={translate("popUp.approve.commentLabel")}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleCancel}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default CancelDocuments;
